import { dashboardApi } from '@/store/api/base/api.base';
import {
    dropConversationTemplate,
    writeConversationDraft,
    writeConversationTemplate,
} from '@/store/api/conversation/email/conversation.draft.cache';
import type {
    TypeConversationDraft,
    TypeConversationDraftInput,
    TypeConversationTemplate,
} from '@/types/conversation/types.conversation.email.draft';

type TypeConversationTemplateInput = { title: string; text: string };

export const conversationDraftApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        listConversationDrafts: build.query<TypeConversationDraft[], void>({
            query: () => ({ url: '/conversations/email/drafts' }),
            providesTags: ['ConversationDraft'],
        }),

        saveConversationDraft: build.mutation<TypeConversationDraft, { id: string | null; draft: TypeConversationDraftInput }>({
            query: ({ id, draft }) => (id
                ? { url: `/conversations/email/drafts/${encodeURIComponent(id)}`, method: 'patch', data: { draft } }
                : { url: '/conversations/email/drafts', method: 'post', data: { draft } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeConversationDraft(dispatch, data);
                } catch {
                }
            },
        }),

        removeConversationDraft: build.mutation<{ id: string }, string>({
            query: (id) => ({ url: `/conversations/email/drafts/${encodeURIComponent(id)}`, method: 'delete' }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
                const undo = dispatch(conversationDraftApi.util.updateQueryData('listConversationDrafts', undefined, (draft) => draft.filter((item) => item.id !== id)));
                try {
                    await queryFulfilled;
                } catch {
                    undo.undo();
                }
            },
        }),

        listConversationTemplates: build.query<TypeConversationTemplate[], void>({
            query: () => ({ url: '/conversations/email/templates' }),
            providesTags: ['ConversationTemplate'],
            keepUnusedDataFor: 600,
        }),

        createConversationTemplate: build.mutation<TypeConversationTemplate, TypeConversationTemplateInput>({
            query: (template) => ({ url: '/conversations/email/templates', method: 'post', data: template }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeConversationTemplate(dispatch, data);
                } catch {
                }
            },
        }),

        updateConversationTemplate: build.mutation<TypeConversationTemplate, { id: string; template: TypeConversationTemplateInput }>({
            query: ({ id, template }) => ({ url: `/conversations/email/templates/${encodeURIComponent(id)}`, method: 'patch', data: template }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    writeConversationTemplate(dispatch, data);
                } catch {
                }
            },
        }),

        removeConversationTemplate: build.mutation<{ id: string }, string>({
            query: (id) => ({ url: `/conversations/email/templates/${encodeURIComponent(id)}`, method: 'delete' }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    dropConversationTemplate(dispatch, id);
                } catch {
                }
            },
        }),
    }),
});

export const {
    useListConversationDraftsQuery, useSaveConversationDraftMutation, useRemoveConversationDraftMutation,
    useListConversationTemplatesQuery, useCreateConversationTemplateMutation, useUpdateConversationTemplateMutation, useRemoveConversationTemplateMutation,
} = conversationDraftApi;

export default conversationDraftApi;
